import {
  applyInstallPriceEdit,
  applyRetailPriceEdit,
  ensureCatalogBaselines,
  getCatalogProductOnlyRetailEach,
  reconcileCatalogMarketingPrices,
} from 'src/utils/retailPriceEdit'

export const isQuotationWriteRow = (product) =>
  product?.entryMode === 'write' ||
  (product?.isAdditionalProduct && product?.entryMode !== 'choose')

const isQuotationChooseEmptyRow = (product) =>
  product?.entryMode === 'choose' && !product?.id

export const getQuotationRowEntryMode = (product) => {
  if (product?.entryMode) return product.entryMode
  if (product?.isAdditionalProduct || product?.is_product_row === 1) return 'write'
  if (product?.id) return 'choose'
  return 'write'
}

/** Catalog "choose" rows eligible for product grouping (not write / empty choose rows). */
export const isQuotationChooseCatalogRow = (product) => {
  if (!product || product?.fromMapServices) return false
  if (isQuotationWriteRow(product)) return false
  if (isQuotationChooseEmptyRow(product)) return false
  if (Number(product?.is_line_catalog) === 1) return true
  return getQuotationRowEntryMode(product) === 'choose' && Boolean(product?.id)
}

export const stripGroupFieldsFromCatalogOption = (item) => {
  if (!item || typeof item !== 'object') return item
  const { group_id, group_name, group_description, group_catalog_anchor_index_id, ...rest } = item
  return rest
}

export const getCustomerOrderProductGroupDisplayName = ({
  groupMeta = {},
  representative = {},
  members = [],
  groupId,
} = {}) => {
  const memberGroupName = (members || []).find(
    (m) => m?.group_name != null && String(m.group_name).trim() !== '',
  )?.group_name

  // Do not fall back to catalog product `name` — that shows member SKUs in Autocomplete chips.
  const representativeName =
    representative?.group_name ||
    (representative?.isGroupAutocompleteChip ? representative?.name : '') ||
    ''

  const resolved =
    groupMeta?.group_name ||
    groupMeta?.groupName ||
    groupMeta?.name ||
    memberGroupName ||
    representativeName ||
    (groupId != null && groupId !== '' ? `Group ${groupId}` : 'Group')

  return String(resolved || 'Group').trim() || 'Group'
}

const getProductGroupIdForAutocomplete = (product) =>
  product?.group_id ?? product?.customer_order_product_group_id ?? product?.groupId ?? null

/** Display label for Products Autocomplete chips/options. */
export const getCatalogAutocompleteDisplayName = (option, customerOrderProductGroups = []) => {
  if (!option) return ''
  const gid = getProductGroupIdForAutocomplete(option)
  if (gid != null && gid !== '') {
    const groupMeta =
      (customerOrderProductGroups || []).find(
        (g) => String(g?.id ?? g?.group_id) === String(gid),
      ) || {}
    return getCustomerOrderProductGroupDisplayName({
      groupMeta,
      representative: option,
      groupId: gid,
    })
  }
  return option?.name || option?.product_name || ''
}

/** Tooltip text for Products Autocomplete chips (group uses group fields, not member product desc). */
export const getCatalogAutocompleteTooltip = (
  option,
  suffix = '',
  customerOrderProductGroups = [],
) => {
  if (!option) return ''
  const suffixPart = suffix ? ` ${suffix}` : ''
  const gid = getProductGroupIdForAutocomplete(option)
  if (option?.isGroupAutocompleteChip || (gid != null && gid !== '')) {
    const name = getCatalogAutocompleteDisplayName(option, customerOrderProductGroups)
    const groupMeta =
      (customerOrderProductGroups || []).find(
        (g) => String(g?.id ?? g?.group_id) === String(gid),
      ) || {}
    const desc = String(
      option.group_description || groupMeta.group_description || groupMeta.description || '',
    ).trim()
    return `${desc || name}${suffixPart}`.trim()
  }
  return `${option.description || ''}${suffixPart}`.trim()
}

/** Collapse grouped catalog lines into one Autocomplete chip per group. */
export const collapseProductsForAutocompleteChips = (
  products = [],
  customerOrderProductGroups = [],
) => {
  const seenGroupIds = new Set()
  const chips = []
  const list = products || []
  const metaById = new Map(
    (customerOrderProductGroups || []).map((g) => [String(g?.id ?? g?.group_id), g]),
  )

  list.forEach((product) => {
    if (!product) return
    const gid = getProductGroupIdForAutocomplete(product)
    if (gid != null && gid !== '') {
      const key = String(gid)
      if (seenGroupIds.has(key)) return
      seenGroupIds.add(key)
      const members = list.filter(
        (p) => p && String(getProductGroupIdForAutocomplete(p)) === key,
      )
      const groupMeta = metaById.get(key) || {}
      const groupName = getCustomerOrderProductGroupDisplayName({
        groupMeta,
        representative: product,
        members,
        groupId: gid,
      })
      const groupDescription =
        product.group_description ||
        members.find(
          (m) => m?.group_description != null && String(m.group_description).trim() !== '',
        )?.group_description ||
        groupMeta.group_description ||
        groupMeta.description ||
        ''
      chips.push({
        ...product,
        group_id: gid,
        isGroupAutocompleteChip: true,
        name: groupName,
        group_name:
          product.group_name ||
          members.find((m) => m?.group_name != null && String(m.group_name).trim() !== '')
            ?.group_name ||
          groupMeta.group_name ||
          groupMeta.name ||
          groupName,
        group_description: groupDescription,
      })
      return
    }
    chips.push(product)
  })

  return chips
}

export const getAutocompleteSelectionKey = (option) => {
  if (!option) return ''
  if (option?.group_id != null && option?.group_id !== '') {
    return `group:${option.group_id}`
  }
  if (option?.indexId != null && option?.indexId !== '') {
    return `row:${option.indexId}`
  }
  return `product:${option?.id ?? option?.name ?? ''}`
}

/** Unique install notes from grouped line members (avoids duplicate text when members share a note). */
export const getGroupInstallNotesDisplay = (members = []) => {
  const seen = new Set()
  const notes = []
  for (const member of members || []) {
    const trimmed = String(member?.install_notes ?? '').trim()
    if (!trimmed) continue
    const key = trimmed.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    notes.push(trimmed)
  }
  return notes.join('\n')
}

/** Group-level notes to copy onto products newly added into an existing group. */
export const getInheritedGroupNotesFromMembers = (members = []) => {
  const productNotes =
    (members || []).find(
      (m) => m?.product_notes != null && String(m.product_notes).trim() !== '',
    )?.product_notes || ''
  const installNotes = getGroupInstallNotesDisplay(members)
  return {
    product_notes: productNotes || '',
    install_notes: installNotes || '',
  }
}

/** Apply existing group notes onto a product joining that group (keep own notes if group has none). */
export const withInheritedGroupNotes = (product, members = []) => {
  const inherited = getInheritedGroupNotesFromMembers(members)
  return {
    ...product,
    product_notes: inherited.product_notes || product?.product_notes || '',
    install_notes: inherited.install_notes || product?.install_notes || '',
  }
}

export const isProductLineCatalog = (product) => {
  if (!product) return false
  return Number(product.is_line_catalog) === 1
}

export const countsAsGroupCatalogEligible = (product) => isQuotationChooseCatalogRow(product)

export const isSameGroupId = (a, b) => a != null && b != null && String(a) === String(b)

export const isSameIndexId = (a, b) =>
  a != null && b != null && String(a) === String(b)

export const isIndexIdInList = (list, indexId) =>
  (list || []).some((id) => isSameIndexId(id, indexId))

export const getProductGroupId = (product) =>
  product?.group_id ?? product?.customer_order_product_group_id ?? product?.groupId ?? null

export const getLosingMoneyRowSx = (theme, isLosingMoneyItem) =>
  isLosingMoneyItem
    ? {
        '& .MuiTableCell-root:not(:nth-of-type(5)):not(:nth-of-type(6)):not(:nth-of-type(12)), & .MuiTableCell-root:not(:nth-of-type(5)):not(:nth-of-type(6)):not(:nth-of-type(12)) .MuiInputBase-input, & .MuiTableCell-root:not(:nth-of-type(5)):not(:nth-of-type(6)):not(:nth-of-type(12)) .MuiFormLabel-root':
          {
            color: `${theme.palette.error.main} !important`,
          },
      }
    : undefined

export const getGroupLosingMoneyFlags = (group, groupProductMarketingValues, groupInstallMarketingValues) => {
  if (!group) {
    return { isGroupProductLosingMoney: false, isGroupInstallLosingMoney: false }
  }
  const groupKey = String(group.id)
  const productMarketing =
    groupProductMarketingValues?.[groupKey] ||
    groupProductMarketingValues?.[group.id] ||
    {}
  const installMarketing =
    groupInstallMarketingValues?.[groupKey] ||
    groupInstallMarketingValues?.[group.id] ||
    {}

  const groupAdjValueType =
    productMarketing.adjValueType ?? group.groupAdjValueType ?? 1
  const groupInstallAdjValueType =
    installMarketing.adjValueType ?? group.groupInstallAdjValueType ?? 1

  const isNegativeGroupProductAdjustmentMode =
    groupAdjValueType === false ||
    groupAdjValueType === 0 ||
    groupAdjValueType === '0' ||
    groupAdjValueType === 'false'
  const isNegativeGroupInstallAdjustmentMode =
    groupInstallAdjValueType === false ||
    groupInstallAdjValueType === 0 ||
    groupInstallAdjValueType === '0' ||
    groupInstallAdjValueType === 'false'

  const productFormulaEach = Number(
    group.baseProductFormulaEach ?? group.totalProductFormulaEach ?? 0,
  )
  const productRetailEach = Number(group.adjustedGroupRetailEach ?? 0)
  const installFormulaEach = Number(
    group.baseInstallFormulaEach ?? group.totalInstallFormulaEach ?? 0,
  )
  const installRetailEach = Number(group.adjustedGroupInstallRetailEach ?? 0)

  return {
    isGroupProductLosingMoney:
      isNegativeGroupProductAdjustmentMode &&
      productRetailEach < productFormulaEach,
    isGroupInstallLosingMoney:
      installRetailEach > 0 &&
      isNegativeGroupInstallAdjustmentMode &&
      installRetailEach < installFormulaEach,
  }
}

export const getGroupAnchorIndexId = (group) => {
  const products = group?.products || []
  const explicit = products.find((p) => p?.group_catalog_anchor_index_id != null)
  if (explicit) {
    const aid = Number(explicit.group_catalog_anchor_index_id)
    if (!Number.isNaN(aid) && products.some((p) => Number(p.indexId) === aid)) {
      return aid
    }
  }
  const line = products.find((p) => isProductLineCatalog(p))
  if (line?.indexId != null) return line.indexId
  const ids = products.map((p) => Number(p.indexId)).filter((n) => !Number.isNaN(n))
  return ids.length ? Math.min(...ids) : null
}

const resolveGroupMetaNumber = (value) => {
  if (value == null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

export const GROUP_QUANTITY_MODE_LEGACY = 'legacy'
export const GROUP_QUANTITY_MODE_SET_MULTIPLIER = 'setMultiplier'

export const getGroupMemberProductQty = (product) =>
  Math.max(
    1,
    Number(
      product?.group_product_quantity ??
        product?.locationQuantity ??
        product?.location_quantity ??
        product?.quantity ??
        1,
    ) || 1,
  )

export const getGroupMemberInstallQty = (product) =>
  Math.max(
    1,
    Number(
      product?.group_install_quantity ??
        product?.install_marketing_quantity ??
        product?.install_quantity ??
        product?.install_qty ??
        1,
    ) || 1,
  )

const getGroupMemberProductRetailEach = (product, markupTypeVal) =>
  markupTypeVal == 1
    ? Number(product?.price || product?.product_price || 0)
    : Number(
        product?.actual_retail_price ||
          product?.retail_price ||
          product?.product_retail_price ||
          0,
      )

const sumGroupMemberProductRetailEach = (products, markupTypeVal) =>
  (products || []).reduce(
    (sum, p) => sum + getGroupMemberProductRetailEach(p, markupTypeVal),
    0,
  )

const sumGroupMemberProductRetailSetSubtotal = (products, markupTypeVal) =>
  (products || []).reduce(
    (sum, p) => sum + getGroupMemberProductRetailEach(p, markupTypeVal) * getGroupMemberProductQty(p),
    0,
  )

const sumGroupMemberInstallRetailEach = (products) =>
  (products || []).reduce((sum, p) => sum + Number(p?.install_cost || 0), 0)

const sumGroupMemberInstallRetailSetSubtotal = (products) =>
  (products || []).reduce(
    (sum, p) => sum + Number(p?.install_cost || 0) * getGroupMemberInstallQty(p),
    0,
  )

export const getSingleProductFormulaCostEach = (product) => {
  if (!product) return 0
  const rawCost =
    product?.product_formula_cost_each != null && product?.product_formula_cost_each !== ''
      ? Number(product.product_formula_cost_each)
      : product?.inhouse === 0
        ? (parseFloat(product?.sub_contract_price) || 0)
        : (
          (parseFloat(product?.expected_material_cost_with_markup) || 0) +
          (parseFloat(product?.expected_service_cost_with_markup) || 0)
        )
  return rawCost || parseFloat(product?.expected_formula_price) || parseFloat(product?.expected_cost) || parseFloat(product?.unit_cost) || 0
}

const sumGroupMemberProductFormulaEach = (products) =>
  (products || []).reduce((sum, p) => sum + getSingleProductFormulaCostEach(p), 0)

const sumGroupMemberProductFormulaSetSubtotal = (products) =>
  (products || []).reduce(
    (sum, p) => sum + getSingleProductFormulaCostEach(p) * getGroupMemberProductQty(p),
    0,
  )

const sumGroupMemberInstallFormulaEach = (products) =>
  (products || []).reduce(
    (sum, p) => sum + Number(p?.expected_install_labor_cost_with_markup || 0),
    0,
  )

const sumGroupMemberInstallFormulaSetSubtotal = (products) =>
  (products || []).reduce(
    (sum, p) =>
      sum +
      Number(p?.expected_install_labor_cost_with_markup || 0) * getGroupMemberInstallQty(p),
    0,
  )

const resolveGroupBaseEach = (memberSum, metaValue, accumulatedValue = 0) => {
  if (memberSum > 0) return memberSum
  const fromMeta = resolveGroupMetaNumber(metaValue)
  if (fromMeta != null) return fromMeta
  return Number(accumulatedValue) || 0
}

export const resolveGroupDisplayBaseEach = resolveGroupBaseEach

const readGroupMarketingAdjustmentNumber = (value) => {
  if (value === '' || value == null) return 0
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

/** Prefer in-session group marketing state; treat explicit empty string as cleared (0). */
export const resolveGroupProductMarketingAdjustment = (productMarketing = {}, meta = {}) => {
  if (productMarketing.adjustment_draft !== undefined && productMarketing.adjustment_draft !== '') {
    return readGroupMarketingAdjustmentNumber(productMarketing.adjustment_draft)
  }
  if (productMarketing.adjustment !== undefined && productMarketing.adjustment !== null) {
    return readGroupMarketingAdjustmentNumber(productMarketing.adjustment)
  }
  return readGroupMarketingAdjustmentNumber(
    meta?.product_marketing_adjustment ?? meta?.productMarketingAdjustment ?? 0,
  )
}

export const resolveGroupInstallMarketingAdjustment = (installMarketing = {}, meta = {}) => {
  if (installMarketing.adjustment_draft !== undefined && installMarketing.adjustment_draft !== '') {
    return readGroupMarketingAdjustmentNumber(installMarketing.adjustment_draft)
  }
  if (installMarketing.adjustment !== undefined && installMarketing.adjustment !== null) {
    return readGroupMarketingAdjustmentNumber(installMarketing.adjustment)
  }
  return readGroupMarketingAdjustmentNumber(
    meta?.install_marketing_adjustment ?? meta?.installMarketingAdjustment ?? 0,
  )
}

export const getGroupProductMarketingAdjustmentDisplay = (
  productMarketing = {},
  computedAdjustment = 0,
) => {
  if (productMarketing.adjustment_draft !== undefined) {
    const draft = String(productMarketing.adjustment_draft ?? '')
    return draft === '' ? '' : draft
  }
  if (productMarketing.adjustment !== undefined && productMarketing.adjustment !== null) {
    if (productMarketing.adjustment === '') return ''
    return String(productMarketing.adjustment)
  }
  const computed = Number(computedAdjustment) || 0
  return computed > 0 ? String(computed) : ''
}

export const getGroupInstallMarketingAdjustmentDisplay = (
  installMarketing = {},
  computedAdjustment = 0,
) => {
  if (installMarketing.adjustment_draft !== undefined) {
    const draft = String(installMarketing.adjustment_draft ?? '')
    return draft === '' ? '' : draft
  }
  if (installMarketing.adjustment !== undefined && installMarketing.adjustment !== null) {
    if (installMarketing.adjustment === '') return ''
    return String(installMarketing.adjustment)
  }
  const computed = Number(computedAdjustment) || 0
  return computed > 0 ? String(computed) : ''
}

export const computeGroupedProducts = ({
  selectedProducts,
  markupType,
  customerOrderProductGroups,
  groupProductMarketingValues,
  groupInstallMarketingValues,
  groupQuantityMode = GROUP_QUANTITY_MODE_LEGACY,
}) => {
  const metaById = new Map()
  ;(customerOrderProductGroups || []).forEach((g) => {
    const id = g?.id ?? g?.group_id
    if (id == null) return
    metaById.set(String(id), g)
  })

  const groupsMap = new Map()

  ;(selectedProducts || []).forEach((p) => {
    const gidRaw = getProductGroupId(p)
    if (gidRaw == null || gidRaw === '') return
    const gid = String(gidRaw)
    const meta = metaById.get(gid) || {}

    if (!groupsMap.has(gid)) {
      groupsMap.set(gid, {
        id: gid,
        name: getCustomerOrderProductGroupDisplayName({
          groupMeta: meta,
          representative: p,
          groupId: gid,
        }),
        description: p.group_description || meta.group_description || meta.description || '',
        products: [],
        totalQty: 0,
        totalSubtotal: 0,
        totalProductFormulaEach: 0,
        totalProductRetailEach: 0,
        totalProductSubtotal: 0,
        totalProductTotal: 0,
        totalInstallQty: 0,
        totalInstallFormulaEach: 0,
        totalInstallRetailEach: 0,
        totalInstallSubtotal: 0,
      })
    }

    const group = groupsMap.get(gid)
    group.products.push(
      p.group_id == null || p.group_id === '' ? { ...p, group_id: gidRaw } : p,
    )
    if (!group.name || group.name === 'Group' || group.name === `Group ${gid}`) {
      group.name = getCustomerOrderProductGroupDisplayName({
        groupMeta: meta,
        representative: p,
        members: group.products,
        groupId: gid,
      })
    }

    const locQty =
      Number(p?.group_product_quantity) ||
      Number(p?.locationQuantity) ||
      Number(p?.quantity) ||
      1
    const installQty =
      Number(p?.group_install_quantity) ||
      Number(p?.install_marketing_quantity) ||
      Number(p?.install_quantity) ||
      1
    const productFormulaEach = getSingleProductFormulaCostEach(p)
    const productRetailEach =
      markupType == 1
        ? Number(p?.price || p?.product_price || 0)
        : markupType == 2
        ? Number(p?.actual_retail_price || p?.retail_price || p?.product_retail_price || 0)
        : 0
    const installFormulaEach = Number(p?.expected_install_labor_cost_with_markup || 0)
    const installRetailEach = Number(p?.install_cost || 0)

    const productSubTotal =
      markupType == 1
        ? (Number(p?.price || p?.product_price || 0)) * locQty
        : markupType == 2
        ? (Number(p?.actual_retail_price || p?.retail_price || p?.product_retail_price || 0)) * locQty
        : 0
    const installSubTotal = installRetailEach * installQty

    const discount = Number(p?.discount || 0)
    const productTotal = Math.max(0, productSubTotal + installSubTotal - discount)
    const productOnlyTotal = Math.max(0, productSubTotal - discount)

    group.totalQty += locQty
    group.totalProductFormulaEach += productFormulaEach
    group.totalProductRetailEach += productRetailEach
    group.totalProductSubtotal += productSubTotal
    group.totalProductTotal += productOnlyTotal
    group.totalInstallQty += installQty
    group.totalInstallFormulaEach += installFormulaEach
    group.totalInstallRetailEach += installRetailEach
    group.totalInstallSubtotal += installSubTotal
    group.totalSubtotal += productTotal
  })

  return Array.from(groupsMap.values()).map((group) => {
    const gid = group.id
    const meta = metaById.get(String(gid)) || {}
    const useSetMultiplier = groupQuantityMode === GROUP_QUANTITY_MODE_SET_MULTIPLIER

    const groupQuantity = useSetMultiplier
      ? Math.max(
          1,
          Number(
            meta?.product_marketing_quantity ??
              meta?.productMarketingQuantity ??
              1,
          ) || 1,
        )
      : Math.max(
          1,
          Number(
            group.products?.[0]?.product_marketing_quantity ??
              group.products?.[0]?.locationQuantity ??
              group.products?.[0]?.quantity ??
              meta?.product_marketing_quantity ??
              1,
          ) || 1,
        )

    const hasManualInstallQty = (group.products || []).some(
      (p) => p?.manually_updated_install_quantity,
    )
    // Set-multiplier: group install qty lives on group meta (independent set count).
    // Legacy: install qty tracks product qty unless a member was manually overridden.
    const groupInstallQuantity = useSetMultiplier
      ? Math.max(
          1,
          Number(
            meta?.install_marketing_quantity ??
              meta?.installMarketingQuantity ??
              groupQuantity,
          ) || 1,
        )
      : hasManualInstallQty
        ? Math.max(
            1,
            Number(
              group.products?.[0]?.install_marketing_quantity ??
                group.products?.[0]?.install_quantity ??
                group.products?.[0]?.group_install_quantity ??
                meta?.install_marketing_quantity ??
                1,
            ) || 1,
          )
        : groupQuantity

    const groupKey = String(gid)
    const productMarketing =
      groupProductMarketingValues?.[groupKey] ||
      groupProductMarketingValues?.[gid] ||
      {}
    const installMarketing =
      groupInstallMarketingValues?.[groupKey] ||
      groupInstallMarketingValues?.[gid] ||
      {}

    const groupAdjustment = resolveGroupProductMarketingAdjustment(productMarketing, meta)
    const groupAdjValueType =
      productMarketing.adjValueType !== undefined
        ? productMarketing.adjValueType
        : meta?.product_marketing_adj_value_type != null
          ? parseMarketingAdjValueType(meta.product_marketing_adj_value_type, true)
          : group.products?.[0]?.adj_value_type ?? true

    const groupInstallAdjustment = resolveGroupInstallMarketingAdjustment(
      installMarketing,
      meta,
    )
    const groupInstallAdjValueType =
      installMarketing.adjValueType !== undefined
        ? installMarketing.adjValueType
        : meta?.install_marketing_adj_value_type != null
          ? parseMarketingAdjValueType(meta.install_marketing_adj_value_type, true)
          : group.products?.[0]?.install_adj_value_type ?? true

    const productSignedAdj =
      groupAdjValueType === false || Number(groupAdjValueType) === 0
        ? -groupAdjustment
        : groupAdjustment

    const installSignedAdj =
      groupInstallAdjValueType === false || Number(groupInstallAdjValueType) === 0
        ? -groupInstallAdjustment
        : groupInstallAdjustment

    const memberProductRetailEachSum = sumGroupMemberProductRetailEach(group.products, markupType)
    const memberInstallRetailEachSum = sumGroupMemberInstallRetailEach(group.products)
    const memberProductFormulaEachSum = sumGroupMemberProductFormulaEach(group.products)
    const memberInstallFormulaEachSum = sumGroupMemberInstallFormulaEach(group.products)
    const memberProductSetSubtotal = sumGroupMemberProductRetailSetSubtotal(group.products, markupType)
    const memberInstallSetSubtotal = sumGroupMemberInstallRetailSetSubtotal(group.products)
    const memberProductFormulaSetSubtotal = sumGroupMemberProductFormulaSetSubtotal(group.products)
    const memberInstallFormulaSetSubtotal = sumGroupMemberInstallFormulaSetSubtotal(group.products)

    const baseProductFormulaEach = resolveGroupBaseEach(
      useSetMultiplier ? memberProductFormulaSetSubtotal : memberProductFormulaEachSum,
      meta?.product_total_formula_each,
      group.totalProductFormulaEach,
    )
    const baseProductEach = resolveGroupBaseEach(
      useSetMultiplier ? memberProductSetSubtotal : memberProductRetailEachSum,
      meta?.product_total_retail_each,
      group.totalProductRetailEach,
    )
    const baseInstallFormulaEach = resolveGroupBaseEach(
      useSetMultiplier ? memberInstallFormulaSetSubtotal : memberInstallFormulaEachSum,
      meta?.install_total_formula_each,
      group.totalInstallFormulaEach,
    )
    const baseInstallEach = resolveGroupBaseEach(
      useSetMultiplier ? memberInstallSetSubtotal : memberInstallRetailEachSum,
      meta?.install_total_retail_each,
      group.totalInstallRetailEach,
    )

    const adjustedProductEach = baseProductEach + productSignedAdj
    const adjustedInstallEach = baseInstallEach + installSignedAdj

    const adjustedGroupRetailEach = adjustedProductEach
    const adjustedGroupInstallRetailEach = adjustedInstallEach

    const adjustedGroupSubtotal = adjustedGroupRetailEach * groupQuantity
    const adjustedGroupInstallSubtotal = adjustedGroupInstallRetailEach * groupInstallQuantity

    const adjustedGroupProductTotal = adjustedGroupSubtotal
    const adjustedGroupTotal = Math.max(
      0,
      adjustedGroupSubtotal + adjustedGroupInstallSubtotal,
    )

    const { groupAllLocationSame, groupIsSingleCard } = resolveGroupLocationFlags(group, meta)
    const metaAddresses = parseGroupAddressesMeta(meta)
    const memberAddresses =
      group.products?.[0]?.location || group.products?.[0]?.addresses || []
    const sourceAddresses =
      (metaAddresses && metaAddresses.length > 0 ? metaAddresses : null) ||
      memberAddresses ||
      []
    const groupAddresses = buildGroupAddressesForProduct(
      {
        ...(group.products?.[0] || {}),
        locationQuantity: groupQuantity,
        location: sourceAddresses,
        addresses: sourceAddresses,
      },
      sourceAddresses,
      {
        allLocationSame: groupAllLocationSame,
        isSingleCard: groupIsSingleCard,
        locationQuantity: groupQuantity,
      },
    )

    return {
      ...group,
      groupMeta: meta,
      baseProductFormulaEach,
      baseProductEach,
      baseInstallFormulaEach,
      baseInstallEach,
      groupQuantity,
      groupInstallQuantity,
      groupAdjValueType,
      groupAdjustment,
      groupInstallAdjValueType,
      groupInstallAdjustment,
      groupAddresses,
      groupAllLocationSame,
      groupIsSingleCard,
      adjustedGroupRetailEach,
      adjustedGroupSubtotal,
      adjustedGroupProductTotal,
      adjustedGroupInstallRetailEach,
      adjustedGroupInstallSubtotal,
      adjustedGroupTotal,
      // Handlers/validation use these names — prefer saved group meta over member sums.
      totalProductFormulaEach: baseProductFormulaEach,
      totalProductRetailEach: baseProductEach,
      totalProductCatalogRetailEach: baseProductEach,
      totalInstallFormulaEach: baseInstallFormulaEach,
      totalInstallRetailEach: baseInstallEach,
      totalInstallCatalogRetailEach: baseInstallEach,
    }
  })
}

export const computeProductTableBodyRenderOrder = (selectedProducts, groupedProducts) => {
  const groupMap = new Map((groupedProducts || []).map((g) => [String(g.id), g]))
  const sorted = [...(selectedProducts || [])].sort(
    (a, b) => (a?.indexId || 0) - (b?.indexId || 0),
  )
  const groupMemberIndexIds = new Set()
  ;(groupedProducts || []).forEach((g) => {
    ;(g.products || []).forEach((p) => {
      if (p?.indexId != null) groupMemberIndexIds.add(Number(p.indexId))
    })
  })
  const out = []
  const seenGroups = new Set()
  for (const p of sorted) {
    const gidRaw = getProductGroupId(p)
    if (gidRaw == null || gidRaw === '') {
      out.push({ type: 'product', indexId: p.indexId })
      continue
    }
    const gid = String(gidRaw)
    if (seenGroups.has(gid)) continue
    const group = groupMap.get(gid)
    if (!group) continue
    const anchorId = getGroupAnchorIndexId(group)
    if (anchorId == null || Number(p.indexId) !== Number(anchorId)) continue
    seenGroups.add(gid)
    out.push({ type: 'group', groupId: gid, indexId: anchorId })
  }
  return out.filter((item) => {
    if (item.type !== 'product') return true
    return !groupMemberIndexIds.has(Number(item.indexId))
  })
}

export const getBlankLocationAddress = (id = 0) => ({
  id,
  address: '',
  city: '',
  state: '',
  zip: '',
})

export const buildGroupAddressesForProduct = (product, sourceAddresses, overrides = {}) => {
  const nextAllLocationSame = overrides.allLocationSame ?? product?.allLocationSame
  const nextIsSingleCard = overrides.isSingleCard ?? product?.is_single_card
  const qtyForCount = overrides.locationQuantity ?? product?.locationQuantity ?? 1
  const targetCount = nextIsSingleCard ? 1 : Math.max(1, Number(qtyForCount || 1))
  const normalizedSource =
    sourceAddresses && sourceAddresses.length > 0
      ? sourceAddresses
      : [getBlankLocationAddress(0)]

  if (nextAllLocationSame) {
    const baseAddress = normalizedSource[0] || getBlankLocationAddress(0)
    return Array(targetCount)
      .fill(null)
      .map((_, i) => ({
        ...getBlankLocationAddress(i),
        ...baseAddress,
        id: i,
        req_loc_id: (product?.location || product?.addresses)?.[i]?.req_loc_id ?? null,
      }))
  }

  return Array(targetCount)
    .fill(null)
    .map((_, i) => ({
      ...getBlankLocationAddress(i),
      ...(normalizedSource[i] || {}),
      id: i,
      req_loc_id: (product?.location || product?.addresses)?.[i]?.req_loc_id ?? null,
    }))
}

/** Parse group_addresses from API/meta (JSON string or array). */
export const parseGroupAddressesMeta = (meta) => {
  const raw = meta?.group_addresses ?? meta?.groupAddresses
  if (!raw) return null
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : null
    } catch (e) {
      return null
    }
  }
  return null
}

export const resolveGroupLocationFlags = (group, meta = {}) => {
  const first = group?.products?.[0]
  const metaAllSame = meta?.group_all_location_same ?? meta?.groupAllLocationSame
  const metaSingle = meta?.group_is_single_card ?? meta?.groupIsSingleCard
  const groupAllLocationSame =
    metaAllSame != null && metaAllSame !== ''
      ? Number(metaAllSame) === 1 || metaAllSame === true
      : !!(first?.allLocationSame)
  const groupIsSingleCard =
    metaSingle != null && metaSingle !== ''
      ? Number(metaSingle) === 1 || metaSingle === true
      : !!(first?.is_single_card)
  return { groupAllLocationSame, groupIsSingleCard }
}

export const normalizeGroupLineQuantity = (value) => {
  let newValue = String(value ?? '')
  if (newValue.startsWith('-')) newValue = newValue.slice(1)
  newValue = newValue.replace(/[^0-9]/g, '')
  if (newValue === '' || Number(newValue) <= 0) return 1
  return Number(newValue)
}

export const getSelectedLineCatalogCount = (selectedProducts, selectedIds = []) => {
  const selectedIdSet = new Set((selectedIds || []).map((id) => String(id)))
  return (selectedProducts || []).reduce((count, product) => {
    if (
      selectedIdSet.has(String(product?.indexId)) &&
      countsAsGroupCatalogEligible(product)
    ) {
      return count + 1
    }
    return count
  }, 0)
}

export const getSelectedLineCatalogProducts = (selectedProducts, selectedIds = []) => {
  const selectedIdSet = new Set((selectedIds || []).map((id) => String(id)))
  return (selectedProducts || []).filter(
    (product) =>
      selectedIdSet.has(String(product?.indexId)) &&
      countsAsGroupCatalogEligible(product),
  )
}

export const getLineProductLabel = (product) =>
  (product?.product_name || product?.name || product?.product_desc || 'Product').trim()

/** Groups still present on the order (excludes deleted / stale API group metadata). */
export const getAddToGroupOptions = ({
  groupedProducts,
  customerOrderProductGroups,
  selectedProducts,
} = {}) => {
  const liveGroupIds = new Set(
    (selectedProducts || [])
      .filter((p) => p?.group_id != null && p?.group_id !== '')
      .map((p) => String(p.group_id)),
  )
  if (liveGroupIds.size === 0) return []

  const fromGrouped = (groupedProducts || [])
    .filter((g) => g?.id != null && liveGroupIds.has(String(g.id)))
    .map((g) => ({
      id: g.id,
      group_id: g.id,
      group_name: g.name || g.group_name,
      name: g.name || g.group_name,
    }))

  const seen = new Set(fromGrouped.map((g) => String(g.id)))
  const fromMeta = (customerOrderProductGroups || [])
    .filter((g) => {
      const id = g?.id ?? g?.group_id
      if (id == null) return false
      const key = String(id)
      return liveGroupIds.has(key) && !seen.has(key)
    })
    .map((g) => {
      const id = g.id ?? g.group_id
      const label = g.group_name || g.name || `Group ${id}`
      return {
        ...g,
        id,
        group_id: id,
        group_name: label,
        name: label,
      }
    })

  return [...fromGrouped, ...fromMeta]
}

/** Selection ids that still map to ungrouped catalog lines (stale ids ignored). */
export const getValidUngroupedSelectionIds = (selectedProducts, groupSelectedProductIds) =>
  (groupSelectedProductIds || []).filter((id) =>
    (selectedProducts || []).some(
      (p) =>
        isSameIndexId(p?.indexId, id) &&
        (p?.group_id == null || p?.group_id === '') &&
        isQuotationChooseCatalogRow(p),
    ),
  )

export const normalizeGroupNameKey = (name) => (name || '').trim().toLowerCase()

export const quotationGroupNameExists = ({
  name,
  customerOrderProductGroups,
  selectedProducts,
  customCatalogGroupSources,
}) => {
  const key = normalizeGroupNameKey(name)
  if (!key) return false
  if (
    (customerOrderProductGroups || []).some(
      (g) => normalizeGroupNameKey(g?.group_name || g?.name) === key,
    )
  ) {
    return true
  }
  const names = new Set()
  ;(selectedProducts || []).forEach((product) => {
    if (product?.group_id && product?.group_name) {
      names.add(normalizeGroupNameKey(product.group_name))
    }
  })
  if (names.has(key)) return true
  if (
    (customCatalogGroupSources || []).some(
      (source) => normalizeGroupNameKey(source?.name) === key,
    )
  ) {
    return true
  }
  return false
}

export const getCatalogSourceCreateOptionLabel = (name = '') => {
  const trimmed = (name || '').trim()
  return trimmed ? `+ Create new group: "${trimmed}"` : '+ Create new group'
}

export const getCatalogSourceOptionLabel = (option) => {
  if (!option) return ''
  if (option.isCreateNew) return getCatalogSourceCreateOptionLabel(option.name)
  if (option.isCustomGroupSource) return (option.name || '').trim()
  return getLineProductLabel(option)
}

export const getActiveGroupNameKeys = ({
  selectedProducts,
  customerOrderProductGroups,
}) => {
  const keys = new Set()
  ;(selectedProducts || []).forEach((product) => {
    if (product?.group_id && product?.group_name) {
      keys.add(normalizeGroupNameKey(product.group_name))
    }
  })
  ;(customerOrderProductGroups || []).forEach((group) => {
    const name = group?.group_name || group?.name
    if (name) keys.add(normalizeGroupNameKey(name))
  })
  return keys
}

export const getCatalogSourceOptions = ({
  selectedProducts,
  groupSelectedProductIds,
}) => {
  // Return ALL selected products, not just catalog-eligible ones.
  const selectedIdSet = new Set((groupSelectedProductIds || []).map((id) => String(id)))
  return (selectedProducts || []).filter(
    (product) => selectedIdSet.has(String(product?.indexId)),
  )
}

export const getCatalogSourceDropdownOptions = ({
  selectedProducts,
  groupSelectedProductIds,
  customCatalogGroupSources,
  customerOrderProductGroups,
  inputValue = '',
}) => {
  const options = getCatalogSourceOptions({
    selectedProducts,
    groupSelectedProductIds,
  })
  const input = (inputValue || '').trim()

  const inputLower = input.toLowerCase()
  const list = input
    ? options.filter((option) =>
        getCatalogSourceOptionLabel(option).toLowerCase().includes(inputLower),
      )
    : options

  const canCreateNew = !quotationGroupNameExists({
    name: input,
    customerOrderProductGroups,
    selectedProducts,
    customCatalogGroupSources,
  })

  if (canCreateNew) {
    return [...list, { isCreateNew: true, name: input, id: '__create_new__' }]
  }

  return list
}

export const attachmentFileKey = (f) => String(f?.url || f?.name || '').trim()

export const QUOTATION_ROW_DELETE_CELL_SX = {
  width: 88,
  minWidth: 88,
  maxWidth: 88,
  px: 0.5,
}

export const QUOTATION_ROW_ACTIONS_BOX_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: 80,
  minWidth: 80,
  gap: 0.5,
}

export const QUOTATION_ROW_ACTIONS_LEADING_SLOT_SX = {
  width: 40,
  height: 40,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

/** Nested group line: stored adjustment, or implied from retail vs catalog baseline. */
export const getNestedLineMarketingAdjustmentValue = (product, markupTypeVal, kind = 'product') => {
  if (!product) return null
  const isInstall = kind === 'install'
  const draftKey = isInstall ? 'install_adjustment_draft' : 'adjustment_draft'
  const adjKey = isInstall ? 'install_adjustment' : 'adjustment'
  const preGroupKey = isInstall ? 'pre_group_install_adjustment' : 'pre_group_adjustment'
  const raw = product?.[draftKey] ?? product?.[adjKey]
  if (raw !== null && raw !== undefined && raw !== '') {
    const num = Number(raw)
    if (Number.isFinite(num) && num !== 0) return num
  }
  if (product?.group_id && product?.[preGroupKey] != null && product?.[preGroupKey] !== '') {
    const preGroupNum = Number(product[preGroupKey])
    if (Number.isFinite(preGroupNum) && preGroupNum !== 0) return preGroupNum
  }
  if (isInstall) {
    const retail = Number(product?.install_cost || 0)
    const original = Number(product?.original_install_cost ?? retail)
    const diff = Number(Math.abs(retail - original).toFixed(2))
    return diff > 0.001 ? diff : null
  }
  const catalogEach = getSingleProductFormulaCostEach(product)
  const retailEach =
    markupTypeVal == 1
      ? Number(product?.price || product?.product_price || 0)
      : Number(
          product?.actual_retail_price ||
            product?.retail_price ||
            product?.product_retail_price ||
            0,
        )
  const originalEach = Number(
    product?.original_actual_retail_price ?? product?.original_retail_price ?? retailEach,
  )
  const diff = Number(Math.abs(retailEach - originalEach).toFixed(2))
  return diff > 0.001 ? diff : null
}

export const formatNestedMarketingAdjustmentDisplay = (value) => {
  if (value == null) return '-'
  const num = Number(value)
  if (!Number.isFinite(num) || num === 0) return '-'
  return num
}

/** DB / line: 1 = positive (Add), 0 = negative (Remove). */
export const parseMarketingAdjValueType = (raw, defaultValue = true) => {
  if (raw === 0 || raw === false || raw === '0') return false
  if (raw === 1 || raw === true || raw === '1') return true
  if (raw != null && raw !== '') return Number(raw) !== 0
  return defaultValue
}

/** Nested group line: stored adj type, or inferred from retail vs catalog baseline. */
export const getNestedLineMarketingAdjValueType = (product, markupTypeVal, kind = 'product') => {
  if (!product) return true
  const isInstall = kind === 'install'
  const typeKey = isInstall ? 'install_adj_value_type' : 'adj_value_type'
  const preGroupTypeKey = isInstall ? 'pre_group_install_adj_value_type' : 'pre_group_adj_value_type'
  const draftKey = isInstall ? 'install_adjustment_draft' : 'adjustment_draft'
  const adjKey = isInstall ? 'install_adjustment' : 'adjustment'
  const preGroupKey = isInstall ? 'pre_group_install_adjustment' : 'pre_group_adjustment'
  const rawAdj = product?.[draftKey] ?? product?.[adjKey]
  const hasStoredAdj =
    rawAdj !== null && rawAdj !== undefined && rawAdj !== '' && Number(rawAdj) !== 0

  if (hasStoredAdj) {
    return parseMarketingAdjValueType(product?.[typeKey], true)
  }

  if (product?.group_id && product?.[preGroupKey] != null && product?.[preGroupKey] !== '') {
    const preGroupNum = Number(product[preGroupKey])
    if (Number.isFinite(preGroupNum) && preGroupNum !== 0) {
      return parseMarketingAdjValueType(product?.[preGroupTypeKey], true)
    }
  }

  if (isInstall) {
    const retail = Number(product?.install_cost || 0)
    const original = Number(product?.original_install_cost ?? retail)
    if (Math.abs(retail - original) > 0.001) {
      return retail > original
    }
  } else {
    const catalogEach = getSingleProductFormulaCostEach(product)
    const retailEach =
      markupTypeVal == 1
        ? Number(product?.price || product?.product_price || 0)
        : Number(
            product?.actual_retail_price ||
              product?.retail_price ||
              product?.product_retail_price ||
              0,
          )
    const originalEach = Number(
      product?.original_actual_retail_price ?? product?.original_retail_price ?? retailEach,
    )
    if (Math.abs(retailEach - originalEach) > 0.001) {
      return retailEach > originalEach
    }
  }

  return parseMarketingAdjValueType(product?.[typeKey], true)
}

/** Nested group line losing-money flags (uses pre_group_* when grouped). */
export const getNestedLineLosingMoneyFlags = (product, markupTypeVal) => {
  if (!product) {
    return { isProductLosingMoney: false, isInstallLosingMoney: false }
  }
  const isNegativeAdjMode = (adjType) =>
    adjType === false ||
    adjType === 0 ||
    adjType === '0' ||
    adjType === 'false'

  const productAdjType = getNestedLineMarketingAdjValueType(product, markupTypeVal, 'product')
  const productFormulaEach = getSingleProductFormulaCostEach(product)
  const productRetailEach =
    markupTypeVal == 1
      ? Number(product?.price || product?.product_price || 0)
      : Number(
          product?.actual_retail_price ||
            product?.retail_price ||
            product?.product_retail_price ||
            0,
        )
  const isProductLosingMoney =
    isNegativeAdjMode(productAdjType) && productRetailEach < productFormulaEach

  const installAdjType = getNestedLineMarketingAdjValueType(product, markupTypeVal, 'install')
  const installFormulaEach = Number(product?.expected_install_labor_cost_with_markup || 0)
  const installRetailEach = Math.max(0, Number(product?.install_cost || 0))
  const isInstallLosingMoney =
    installRetailEach > 0 &&
    isNegativeAdjMode(installAdjType) &&
    installRetailEach < installFormulaEach

  return { isProductLosingMoney, isInstallLosingMoney }
}

/** Snapshot line marketing before group membership clears adjustment fields. */
export const snapshotGroupMemberMarketing = (product) => ({
  pre_group_adjustment: product?.adjustment ?? 0,
  pre_group_adj_value_type: product?.adj_value_type ?? true,
  pre_group_install_adjustment: product?.install_adjustment ?? 0,
  pre_group_install_adj_value_type: product?.install_adj_value_type ?? true,
})

/** Preserve existing pre_group snapshot; only snapshot from live fields when missing. */
export const ensureGroupMemberMarketingSnapshot = (product) => {
  if (
    Object.prototype.hasOwnProperty.call(product || {}, 'pre_group_adjustment') ||
    Object.prototype.hasOwnProperty.call(product || {}, 'pre_group_install_adjustment')
  ) {
    return {}
  }
  return snapshotGroupMemberMarketing(product)
}

/** Hydrate a saved grouped line: store member marketing in pre_group and clear live adjustment fields. */
export const hydrateGroupedMemberMarketingFromRow = (row) => {
  const groupId = row?.group_id ?? row?.customer_order_product_group_id ?? null
  if (groupId == null || groupId === '') return {}

  return {
    ...snapshotGroupMemberMarketing({
      adjustment: row?.adjustment,
      adj_value_type: row?.adj_value_type,
      install_adjustment: row?.install_adjustment,
      install_adj_value_type: row?.install_adj_value_type,
    }),
    adjustment: 0,
    adj_value_type: true,
    install_adjustment: 0,
    install_adj_value_type: true,
  }
}

/** Restore nested line marketing from pre-group snapshot before API save. */
export const resolveGroupMemberMarketingForSave = (product, markupTypeVal) => {
  if (!product?.group_id) return product

  const hasPreGroupProduct = Object.prototype.hasOwnProperty.call(
    product,
    'pre_group_adjustment',
  )
  const hasPreGroupInstall = Object.prototype.hasOwnProperty.call(
    product,
    'pre_group_install_adjustment',
  )
  if (!hasPreGroupProduct && !hasPreGroupInstall) return product

  const liveProductAdj = Number(product.adjustment) || 0
  const preGroupProductAdj = Number(product.pre_group_adjustment) || 0
  const liveInstallAdj = Number(product.install_adjustment) || 0
  const preGroupInstallAdj = Number(product.pre_group_install_adjustment) || 0

  const needsProductRestore = liveProductAdj === 0 && preGroupProductAdj !== 0
  const needsInstallRestore = liveInstallAdj === 0 && preGroupInstallAdj !== 0

  if (!needsProductRestore && !needsInstallRestore) {
    return product
  }

  return applySnapshotMarketingToProduct(ensureCatalogBaselines(product), markupTypeVal)
}

const applySnapshotMarketingToProduct = (product, markupTypeVal) => {
  let next = { ...product }
  const productAdj = Number(product?.pre_group_adjustment)
  if (Number.isFinite(productAdj) && productAdj !== 0) {
    const isPositive = parseMarketingAdjValueType(product?.pre_group_adj_value_type, true)
    const signedAdj = isPositive ? productAdj : -productAdj
    const originalActual = Number(
      next.original_actual_retail_price ?? next.original_product_retail_price ?? 0,
    )
    const originalRetail = Number(next.original_retail_price ?? originalActual)
    const originalPrice = Number(next.original_price ?? next.price ?? 0)
    next = {
      ...next,
      adjustment: productAdj,
      adj_value_type: isPositive,
      retail_price_edited: true,
      actual_retail_price: Number((originalActual + signedAdj).toFixed(2)),
      product_retail_price: Number((originalActual + signedAdj).toFixed(2)),
      retail_price: Number((originalRetail + signedAdj).toFixed(2)),
      ...(markupTypeVal == 1
        ? { price: Number((originalPrice + signedAdj).toFixed(2)), product_price: Number((originalPrice + signedAdj).toFixed(2)) }
        : {}),
    }
  }

  const installAdj = Number(product?.pre_group_install_adjustment)
  if (Number.isFinite(installAdj) && installAdj !== 0 && next.install_cost != null) {
    const isPositive = parseMarketingAdjValueType(product?.pre_group_install_adj_value_type, true)
    const signedAdj = isPositive ? installAdj : -installAdj
    const currentInstall = Number(next.install_cost) || 0
    let originalInstall =
      next.original_install_cost != null && next.original_install_cost !== ''
        ? Number(next.original_install_cost)
        : NaN
    const formulaInstall = Number(
      next.expected_install_labor_cost_with_markup ?? next.install_formula_cost_each ?? 0,
    )
    // Prefer formula baseline when current/original already include marketing (e.g. 100 or 137.50 vs 62.50+37.5)
    if (formulaInstall > 0 && Math.abs(signedAdj) > 0.005) {
      const onceAdjusted = Number((formulaInstall + signedAdj).toFixed(2))
      const twiceAdjusted = Number((onceAdjusted + signedAdj).toFixed(2))
      if (
        Math.abs(currentInstall - onceAdjusted) < 0.005 ||
        Math.abs(currentInstall - twiceAdjusted) < 0.005 ||
        (Number.isFinite(originalInstall) && Math.abs(originalInstall - onceAdjusted) < 0.005)
      ) {
        originalInstall = formulaInstall
      }
    }
    // Missing or stale original (already equals adjusted install) → derive baseline
    if (!Number.isFinite(originalInstall) || Math.abs(originalInstall - currentInstall) < 0.005) {
      originalInstall = Number((currentInstall - signedAdj).toFixed(2))
    }
    const expectedInstall = Number((originalInstall + signedAdj).toFixed(2))
    next = {
      ...next,
      install_adjustment: installAdj,
      install_adj_value_type: isPositive,
      install_price_edited: true,
      original_install_cost: originalInstall,
      install_cost: expectedInstall,
    }
  }

  return next
}

const clearGroupMemberMarketingSnapshot = (product) => {
  const {
    pre_group_adjustment,
    pre_group_adj_value_type,
    pre_group_install_adjustment,
    pre_group_install_adj_value_type,
    ...rest
  } = product || {}
  return rest
}

/** When a line leaves a group, restore product/install marketing from pre-group snapshot when available. */
export const restoreUngroupedProductMarketing = (product, markupTypeVal, options = {}) => {
  const { hasNegativePermission = true } = options
  if (!product) return product

  let next = ensureCatalogBaselines({
    ...product,
    group_id: null,
    group_name: null,
    group_description: null,
    group_catalog_anchor_index_id: null,
    customer_order_product_group_id: null,
    group_product_quantity: undefined,
    group_install_quantity: undefined,
  })

  const hasMarketingSnapshot =
    Object.prototype.hasOwnProperty.call(product || {}, 'pre_group_adjustment') ||
    Object.prototype.hasOwnProperty.call(product || {}, 'pre_group_install_adjustment')

  if (hasMarketingSnapshot) {
    const productAdj = Number(product.pre_group_adjustment) || 0
    const installAdj = Number(product.pre_group_install_adjustment) || 0

    if (productAdj === 0) {
      next = reconcileCatalogMarketingPrices({
        ...next,
        adjustment: 0,
        adj_value_type: parseMarketingAdjValueType(product.pre_group_adj_value_type, true),
        retail_price_edited: false,
        adjustment_draft: undefined,
        retail_price_draft: undefined,
      })
    } else {
      next = applySnapshotMarketingToProduct(
        { ...next, adjustment: 0, retail_price_edited: false },
        markupTypeVal,
      )
    }

    if (installAdj === 0) {
      next = reconcileCatalogMarketingPrices({
        ...next,
        install_adjustment: 0,
        install_adj_value_type: parseMarketingAdjValueType(
          product.pre_group_install_adj_value_type,
          true,
        ),
        install_price_edited: false,
        install_adjustment_draft: undefined,
        install_price_draft: undefined,
      })
    } else if (next.install_cost != null) {
      next = applySnapshotMarketingToProduct(
        { ...next, install_adjustment: 0, install_price_edited: false },
        markupTypeVal,
      )
    }

    return clearGroupMemberMarketingSnapshot(reconcileCatalogMarketingPrices(next))
  }

  const productRetail =
    markupTypeVal == 1
      ? Number(next.price || next.product_price || 0)
      : getCatalogProductOnlyRetailEach(next)

  if (productRetail > 0) {
    const productEdit = applyRetailPriceEdit(next, String(productRetail), markupTypeVal, {
      hasNegativePermission,
    })
    if (productEdit && !productEdit.error) {
      next = { ...next, ...productEdit, retail_price_draft: undefined, adjustment_draft: undefined }
    }
  }

  const installRetail = Number(next.install_cost || 0)
  if (installRetail > 0) {
    const installEdit = applyInstallPriceEdit(next, String(installRetail), {
      hasNegativePermission,
    })
    if (installEdit && !installEdit.error) {
      next = {
        ...next,
        ...installEdit,
        install_price_draft: undefined,
        install_adjustment_draft: undefined,
      }
    }
  }

  return clearGroupMemberMarketingSnapshot(reconcileCatalogMarketingPrices(next))
}

const defaultAttachmentFileKey = (f) => String(f?.url || f?.name || f?.file_url || '').trim()

export const resolveQuotationGroupDisplay = (product, groupedProductsMap) => {
  const groupId = getProductGroupId(product)
  if (!groupId) return null
  const group = groupedProductsMap?.get(String(groupId))
  const groupName =
    group?.name ||
    group?.group_name ||
    product?.group_name ||
    product?.groupName ||
    'Group'
  return { groupId, groupName }
}

export const mapQuotationProductAttachmentForDisplay = (file, product, groupedProductsMap) => {
  const groupDisplay = resolveQuotationGroupDisplay(product, groupedProductsMap)
  const productIndexId = file?.product_index_id ?? product?.indexId ?? product?.newIndexId
  const location_ids =
    product?.location
      ?.map((loc) => loc.id || loc.request_location_id)
      .filter(Boolean)
      .join(',') || String(productIndexId || '')
  const productName = product?.product_name || product?.name || ''

  const base = {
    ...file,
    url: file?.url || file?.name || file?.file_url,
    name: file?.name || file?.url || file?.file_url,
    location_ids: file?.location_ids || location_ids,
    _productAttachment: true,
    product_index_id: productIndexId,
  }

  if (groupDisplay) {
    return {
      ...base,
      productNames: [{ product_id: groupDisplay.groupId, product_name: groupDisplay.groupName }],
      _groupAttachment: true,
    }
  }

  return {
    ...base,
    productNames:
      file?.productNames ||
      (productName
        ? [
            {
              product_id: product?.product_id || product?.id || productIndexId,
              product_name: productName,
            },
          ]
        : []),
    _groupAttachment: false,
  }
}

export const enrichQuotationAttachmentForDisplay = (
  file,
  productIndexId,
  selectedProducts,
  groupedProductsMap,
) => {
  const product = (selectedProducts || []).find(
    (p) => Number(p?.indexId) === Number(productIndexId),
  )
  if (!product) return file
  return mapQuotationProductAttachmentForDisplay(file, product, groupedProductsMap)
}

const mapQuotationServiceAttachmentForDisplay = (file, additionalSelectedServices) => {
  const svc =
    (additionalSelectedServices || []).find(
      (s) => String(s.newIndexId ?? s.indexId) === String(file.service_index_id),
    ) || null
  const serviceName = svc?.name || svc?.service_name || ''
  const serviceIndexId = file.service_index_id ?? svc?.newIndexId ?? svc?.indexId
  const fileUrl = file.url || file.name
  return {
    ...file,
    url: fileUrl,
    name: file.name || fileUrl,
    service_index_id: serviceIndexId,
    serviceNames: file.serviceNames || (serviceName ? [{ service_name: serviceName }] : []),
    _serviceAttachment: true,
  }
}

export const buildQuotationUploadDesignsDisplayFiles = ({
  avatarUrl,
  productAttachments,
  serviceAttachments,
  additionalSelectedServices,
  selectedProducts,
  groupedProductsMap,
  attachmentFileKey = defaultAttachmentFileKey,
}) => {
  const allProductAttachmentFiles = Object.values(productAttachments || {}).flat()
  const allServiceAttachmentFiles = Object.values(serviceAttachments || {}).flat()
  const productAttachmentKeys = new Set(
    allProductAttachmentFiles.map(attachmentFileKey).filter(Boolean),
  )
  const serviceAttachmentKeys = new Set(
    allServiceAttachmentFiles.map(attachmentFileKey).filter(Boolean),
  )

  const availableAvatarFiles = (avatarUrl || []).filter((file) => {
    const fileKey = attachmentFileKey(file)
    return (
      (!fileKey || !productAttachmentKeys.has(fileKey)) &&
      (!fileKey || !serviceAttachmentKeys.has(fileKey))
    )
  })

  const productPreviewFiles = (selectedProducts || []).flatMap((product) => {
    const key = product.indexId
    const list = productAttachments?.[key] || productAttachments?.[String(key)] || []
    return list.map((file) =>
      mapQuotationProductAttachmentForDisplay(file, product, groupedProductsMap),
    )
  })

  const seenKeys = new Set()
  const dedupedProductFiles = productPreviewFiles.filter((file) => {
    const key = attachmentFileKey(file)
    if (!key) return true
    if (seenKeys.has(key)) return false
    seenKeys.add(key)
    return true
  })

  const serviceFiles = allServiceAttachmentFiles.map((file) =>
    mapQuotationServiceAttachmentForDisplay(file, additionalSelectedServices),
  )

  return [...availableAvatarFiles, ...dedupedProductFiles, ...serviceFiles]
}
